<script setup lang="ts">
  import { ref, computed, onMounted, inject, provide } from 'vue'
  import { useRouter } from 'vue-router'
  import { ChevronRight, Plus, MoreHorizontal, ChevronRightIcon } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { EmailsService, type TEmail } from '@/types/generated'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import EmailPopover from '@/components/app/email/emailPopover.vue'

  const router = useRouter()
  const { toast } = useToast()
  const caseStore = useCaseStore()
  const isLoading = ref(false)
  const emailHistory = ref<TEmail[]>([])
  const showEmailPopover = ref(false)
  const replyData = ref(null)

  // Computed property to get the latest email
  const latestEmail = computed(() => {
    if (emailHistory.value.length === 0) return null
    return emailHistory.value[0] // Assuming emails are already sorted by date
  })

  // Get the initials for avatar
  const getInitials = (name: string) => {
    if (!name) return 'N/A'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    return (
      new Date(dateString).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) + ' Uhr'
    )
  }

  // Fetch email history from backend
  const fetchEmailHistory = async () => {
    try {
      isLoading.value = true
      // Get case ID if available
      const caseId = caseStore.caseRecord?.uuid

      // Fetch emails related to this case
      const response = await EmailsService.emailsList({
        _case: caseId
      })

      // Sort by date, newest first
      emailHistory.value = response.results.sort(
        (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
      )
    } catch (error) {
      console.error('Error fetching emails:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'E-Mails konnten nicht geladen werden'
      })
    } finally {
      isLoading.value = false
    }
  }

  // Format the email content for display
  const formatEmailContent = (html: string) => {
    if (!html) return []

    // Create a temporary div to parse the HTML
    const div = document.createElement('div')
    div.innerHTML = html

    // Get the text content and split by line breaks
    const text = div.textContent || ''
    return text.split('\n').filter((line) => line.trim() !== '')
  }

  // Navigate to email history view
  const viewEmailHistory = () => {
    if (caseStore.caseRecord?.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/communication`)
    }
  }

  // Show the email popover when createNewEmail is called
  const createNewEmail = () => {
    replyData.value = null
    showEmailPopover.value = true
  }

  // Handle email sent event
  const handleEmailSent = () => {
    fetchEmailHistory() // Refresh email history after sending new email
  }

  // Load emails when component mounts
  onMounted(async () => {
    await fetchEmailHistory()
  })

  const registerWidgetActionHandler = inject('registerWidgetActionHandler')
  onMounted(() => {
    if (registerWidgetActionHandler) {
      registerWidgetActionHandler('EMAILS', () => {
        createNewEmail()
      })
    }
  })
</script>

<template>
  <div class="emails-widget">
    <!-- Email Popover Component -->
    <EmailPopover
      v-model:isOpen="showEmailPopover"
      :reply-data="replyData"
      @email-sent="handleEmailSent"
    />

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="flex justify-center py-3"
    >
      <div
        class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-purple-600"
      ></div>
    </div>

    <!-- No Emails State -->
    <div
      v-else-if="!latestEmail"
      class="flex flex-col items-center justify-center rounded-lg border border-gray-300 px-4 py-10 shadow-sm"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          width="40"
          height="40"
          rx="8"
          fill="#F3E8FF"
        />
        <path
          d="M19 20H11M24 14H11M24 26H11M27 18L23 22M23 18L27 22"
          stroke="#7E22CE"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <p class="text-md mt-5 text-center text-gray-500">Keine Rechtsbezogene Daten extrahiert</p>
    </div>

    <!-- Email Content -->
    <div
      v-else
      class="rounded-lg border border-gray-200 p-4"
    >
      <!-- Email Header with Avatar, Sender Info and Date -->
      <div class="mb-2 flex items-start justify-between">
        <div class="flex items-center gap-2">
          <!-- Avatar with Initials -->
          <div
            class="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300 text-white"
          >
            <span class="text-sm font-medium">{{ getInitials(latestEmail.from_email.name) }}</span>
          </div>

          <!-- Sender Info -->
          <div>
            <h3 class="text-sm font-semibold text-gray-900">{{ latestEmail.from_email.name }}</h3>
            <p class="text-xs text-gray-600">{{ latestEmail.from_email.email }}</p>
          </div>
        </div>

        <!-- Date -->
        <div class="text-xs text-gray-600">
          {{ formatDate(latestEmail.sent_at) }}
        </div>
      </div>

      <!-- Email Subject -->
      <h4 class="mb-1 text-sm font-semibold text-gray-900">{{ latestEmail.subject }}</h4>

      <!-- Email Content -->
      <div class="text-sm">
        <div
          class="prose prose-sm max-w-none text-gray-700"
          v-html="latestEmail.html"
        ></div>
      </div>
    </div>

    <!-- View Email History Link -->
    <div class="items-left mt-4 flex justify-end">
      <a
        href="#"
        class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
        @click.prevent="viewEmailHistory"
      >
        E-Mail Verlauf ansehen
        <ChevronRightIcon class="ml-1 h-4 w-4" />
      </a>
    </div>
  </div>
</template>

<style scoped>
  .emails-widget {
    width: 100%;
    box-sizing: border-box;
  }

  /* Override prose styles to match the design */
  :deep(.prose) {
    font-size: 1.125rem;
    line-height: 1.75;
  }

  :deep(.prose p) {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }
</style>
